import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Legend,
  Tooltip,
} from 'chart.js';
import { useLang } from '../LanguageContext';
import {
  formatDeviceTimestamp,
  formatChartRoundHourTime,
  formatChartAxisTimeSuppressDuplicates,
} from '../utils/timeUtils';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';
import { isRateLimitBackoffActive } from '../utils/rateLimitBackoff';
import { emitJoinFarmRooms, subscribeSocketFarmRooms } from '../utils/farmSocketRooms';
import { getSocketIoServerUrl, SOCKET_IO_CLIENT_OPTIONS } from '../utils/socketIoClientOptions';
import {
  EDITABLE_WATER_THRESHOLD_KEYS,
  buildEmptyWaterThresholdState,
  buildWaterConfigDraft,
  buildWaterThresholdState,
  serializeWaterConfig,
  serializeWaterThreshold,
} from '../utils/waterThresholds';

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Legend,
  Tooltip
);

let sharedWaterPageSocket = null;

function decode(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (_) {
    return {};
  }
}

function getSharedWaterPageSocket() {
  if (!sharedWaterPageSocket) {
    sharedWaterPageSocket = io(getSocketIoServerUrl(), SOCKET_IO_CLIENT_OPTIONS);
    subscribeSocketFarmRooms(sharedWaterPageSocket);
  }
  return sharedWaterPageSocket;
}

function deviceIdsEqual(a, b) {
  const left = String(a || '').trim();
  const right = String(b || '').trim();
  if (!left || !right) return false;
  return left === right || left.toLowerCase() === right.toLowerCase();
}

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function asPlainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function getWaterPayloadDeviceId(payload = {}) {
  const safePayload = asPlainObject(payload);
  return String(safePayload.deviceId || safePayload.device_id || safePayload.id || '').trim();
}

function telemetryTimeMs(source = {}) {
  const ts =
    source.lastRealSensorUpdateAt ||
    source.latestTelemetryAt ||
    source.lastTelemetryAt ||
    source.latestTelemetry?.timestamp ||
    source.timestamp ||
    source.ts ||
    null;
  const ms = ts ? new Date(ts).getTime() : 0;
  return Number.isFinite(ms) ? ms : 0;
}

function waterSocketDebug(...args) {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('waterSocketDebug') === '1') {
      console.debug('[water:socket]', ...args);
    }
  } catch (_) {
    /* debug only */
  }
}

function isWaterControllerToken(value) {
  const token = String(value || '').trim().toLowerCase();
  return !token || token === 'water' || token.includes('water') || token.includes('hydro');
}

function mergeWaterReading(prev = {}, payload = {}, options = {}) {
  const previous = asPlainObject(prev);
  const incoming = asPlainObject(payload);
  const latestTelemetry = asPlainObject(incoming.latestTelemetry);
  const data = asPlainObject(incoming.data);
  const source = {
    ...data,
    ...incoming,
    ...latestTelemetry,
  };
  const timestamp =
    incoming.lastRealSensorUpdateAt ||
    incoming.latestTelemetryAt ||
    incoming.lastTelemetryAt ||
    latestTelemetry.timestamp ||
    incoming.timestamp ||
    incoming.ts ||
    previous.timestamp ||
    new Date().toISOString();
  const incomingMs = timestamp ? new Date(timestamp).getTime() : 0;
  const prevMs = telemetryTimeMs(previous);
  if (Number.isFinite(incomingMs) && incomingMs > 0 && prevMs > 0 && incomingMs < prevMs) {
    waterSocketDebug('stale payload ignored', {
      source: options.source || 'unknown',
      deviceId: getWaterPayloadDeviceId(incoming),
      incoming: timestamp,
      previous: previous.lastRealSensorUpdateAt || previous.latestTelemetryAt || previous.lastTelemetryAt || previous.timestamp,
    });
    return previous;
  }

  return {
    ...previous,
    ...incoming,
    deviceId: firstDefined(incoming.deviceId, incoming.device_id, incoming.id, previous.deviceId),
    timestamp,
    ts: firstDefined(incoming.ts, timestamp),
    ph: firstDefined(source.ph, source.pH, source.PH, previous.ph),
    ec: firstDefined(source.ec, source.EC, source.ec_mscm, previous.ec),
    orp: firstDefined(source.orp, source.ORP, source.orp_mv, previous.orp),
    ecTemp: firstDefined(source.ecTemp, source.temp, source.temperature, source.temp_c, previous.ecTemp),
    temp: firstDefined(source.temp, source.ecTemp, source.temperature, source.temp_c, previous.temp),
    temperature: firstDefined(source.temperature, source.temp, source.ecTemp, source.temp_c, previous.temperature),
    level: firstDefined(source.level, source.waterLevel, source.water_level, previous.level),
    levelCm: firstDefined(source.levelCm, source.level_cm, previous.levelCm),
    volume: firstDefined(source.volume, previous.volume),
    levelVoltage: firstDefined(source.levelVoltage, source.level_voltage, previous.levelVoltage),
    phError: firstDefined(source.phError, previous.phError),
    ecError: firstDefined(source.ecError, previous.ecError),
    orpError: firstDefined(source.orpError, previous.orpError),
    tankMaxHeight: firstDefined(source.tankMaxHeight, previous.tankMaxHeight),
    tankFullVolume: firstDefined(source.tankFullVolume, previous.tankFullVolume),
  };
}

export default function WaterDevicePage() {
  const { deviceId } = useParams();
  const { t, lang } = useLang();
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  if (!token) return <Navigate to="/login" replace />;
  const role = user?.role || decode(token).role;
  const isAdmin = role === 'admin' || role === 'superuser';

  const [info, setInfo] = useState(null);
  const [latest, setLatest] = useState(null);
  const [liveWaterReading, setLiveWaterReading] = useState(null);
  const [series, setSeries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [config, setConfig] = useState(null);
  const [range, setRange] = useState('2h');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [pendingFn, setPendingFn] = useState(null);
  const [msg, setMsg] = useState(null);
  const refreshRefs = useRef({});
  const [dispInterval, setDispInterval] = useState('');
  const [srvInterval, setSrvInterval] = useState('');
  const [calibIn, setCalibIn] = useState('');
  const [calibOut, setCalibOut] = useState('');
  const [savingWaterSettings, setSavingWaterSettings] = useState(false);
  const [waterThresholds, setWaterThresholds] = useState(() => buildEmptyWaterThresholdState());
  const [waterConfigDraft, setWaterConfigDraft] = useState({
    tankMaxHeight: '',
    tankFullVolume: '',
    dMin: '',
    sMin: '',
  });
  const isRtl = lang === 'he';
  const actionGapClass = isRtl ? 'ms-2' : 'me-2';
  const rangeLabels = {
    '2h': t('waterPage.range2h'),
    '24h': t('waterPage.range24h'),
    '7d': t('waterPage.range7d'),
    custom: t('waterPage.rangeCustom'),
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    const locale = lang === 'he' ? 'he-IL' : lang === 'es' ? 'es-ES' : lang;
    return formatDeviceTimestamp(ts, locale);
  };

  const handleUnauthorized = useCallback(() => {
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  const getRange = () => {
    const now = new Date();
    let from;
    let to = now;
    let step = '1m';
    if (range === '2h') {
      from = new Date(now.getTime() - 2 * 3600 * 1000);
      step = '1m';
    } else if (range === '24h') {
      from = new Date(now.getTime() - 24 * 3600 * 1000);
      step = '5m';
    } else if (range === '7d') {
      from = new Date(now.getTime() - 7 * 24 * 3600 * 1000);
      step = '15m';
    } else {
      from = customFrom ? new Date(customFrom) : null;
      to = customTo ? new Date(customTo) : null;
      const diff = to && from ? to - from : 0;
      const minutes = diff / 60000 / 300;
      if (minutes <= 1) step = '1m';
      else if (minutes <= 5) step = '5m';
      else if (minutes <= 15) step = '15m';
      else step = '1h';
    }
    return { from, to, step };
  };

  const fetchInfo = () => {
    authFetch('/api/water-in-out/devices', {}, { token, onUnauthorized: handleUnauthorized })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        const d = Array.isArray(list)
          ? list.find((x) => x.deviceId === deviceId)
          : null;
        setInfo(d || null);
      })
      .catch(() => {
        setWaterThresholds(buildEmptyWaterThresholdState());
        setWaterConfigDraft({
          tankMaxHeight: '',
          tankFullVolume: '',
          dMin: '',
          sMin: '',
        });
      });
  };

  const fetchLatest = () => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), 10000);
    authFetch(
      `/api/water-in-out/latest?deviceId=${encodeURIComponent(deviceId)}`,
      { signal: controller.signal },
      { token, onUnauthorized: handleUnauthorized }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setLatest(d))
      .catch(() => {})
      .finally(() => clearTimeout(id));
  };

  const fetchWaterTelemetryLatest = () => {
    authFetch(
      `/api/sensor-data/water/latest/${encodeURIComponent(deviceId)}`,
      {},
      { token, onUnauthorized: handleUnauthorized }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) setLiveWaterReading((prev) => mergeWaterReading(prev, d, { source: 'poll' }));
      })
      .catch(() => {});
  };

  const fetchSeries = () => {
    const { from, to, step } = getRange();
    if (!from || !to) return;
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), 10000);
    const qs = new URLSearchParams({
      deviceId,
      from: from.toISOString(),
      to: to.toISOString(),
      step,
    });
    authFetch(
      `/api/water-in-out/series?${qs.toString()}`,
      { signal: controller.signal },
      { token, onUnauthorized: handleUnauthorized }
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setSeries(d))
      .catch(() => {})
      .finally(() => clearTimeout(id));
    authFetch(
      `/api/water-in-out/summary?${new URLSearchParams({
        deviceId,
        from: from.toISOString(),
        to: to.toISOString(),
      }).toString()}`,
      {},
      { token, onUnauthorized: handleUnauthorized }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setSummary(d))
      .catch(() => {});
  };

  const fetchConfig = () => {
    authFetch(
      `/api/water-in-out/config?deviceId=${deviceId}`,
      {},
      { token, onUnauthorized: handleUnauthorized }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setConfig(d))
      .catch(() => {});
    authFetch(
      `/api/device/${encodeURIComponent(deviceId)}/live-config?refresh=0`,
      {},
      { token, onUnauthorized: handleUnauthorized }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setWaterThresholds(buildWaterThresholdState(d.thresholds || {}, d.config || {}));
        const nextConfigDraft = buildWaterConfigDraft(d.config || {});
        setWaterConfigDraft(nextConfigDraft);
        setDispInterval(nextConfigDraft.dMin);
        setSrvInterval(nextConfigDraft.sMin);
      })
      .catch(() => {});
  };

  const requestControllerConfig = () =>
    authFetch(
      `/api/device/${encodeURIComponent(deviceId)}/live-config?refresh=1`,
      {},
      { token, onUnauthorized: handleUnauthorized }
    ).catch(() => null);

  const setWaterThresholdField = (metric, part, value) => {
    setWaterThresholds((prev) => ({
      ...prev,
      [metric]: {
        ...prev[metric],
        [part]: value,
      },
    }));
  };

  const setWaterConfigField = (key, value) => {
    setWaterConfigDraft((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const saveWaterSettings = async () => {
    setSavingWaterSettings(true);
    try {
      const thresholdPayload = {};
      EDITABLE_WATER_THRESHOLD_KEYS.forEach((key) => {
        const value = waterThresholds[key];
        const serialized = serializeWaterThreshold(value);
        if (serialized) thresholdPayload[key] = serialized;
      });
      const response = await authFetch(
        `/api/device/${deviceId}/settings`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            thresholds: thresholdPayload,
            config: serializeWaterConfig(waterConfigDraft),
          }),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('waterPage.genericError'));
      }
      setMsg({ type: 'success', text: t('waterPage.successOk') });
      const live = await requestControllerConfig();
      if (live?.ok) {
        const d = await live.json();
        setWaterThresholds(buildWaterThresholdState(d.thresholds || {}, d.config || {}));
        const nextConfigDraft = buildWaterConfigDraft(d.config || {});
        setWaterConfigDraft(nextConfigDraft);
        setDispInterval(nextConfigDraft.dMin);
        setSrvInterval(nextConfigDraft.sMin);
      }
      fetchConfig();
    } catch (err) {
      setMsg({ type: 'error', text: err?.message || t('waterPage.genericError') });
    } finally {
      setSavingWaterSettings(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const callFn = (name, arg = '') => {
    setPendingFn(name);
    authFetch(
      `/api/device/${deviceId}/callFunction`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ functionName: name, arg: String(arg) }),
      },
      { token, onUnauthorized: handleUnauthorized }
    )
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok && parseInt(data.return_value, 10) === 1) {
          setMsg({ type: 'success', text: t('waterPage.successOk') });
          fetchLatest();
          fetchWaterTelemetryLatest();
          fetchConfig();
        } else {
          setMsg({ type: 'error', text: data.error || t('waterPage.genericError') });
        }
      })
      .catch(() => setMsg({ type: 'error', text: t('waterPage.genericError') }))
      .finally(() => {
        setPendingFn(null);
        setTimeout(() => setMsg(null), 3000);
      });
  };

  useEffect(() => {
    fetchInfo();
    fetchLatest();
    fetchWaterTelemetryLatest();
    fetchSeries();
    fetchConfig();
  }, [deviceId, range]);

  useEffect(() => {
    const socket = getSharedWaterPageSocket();
    emitJoinFarmRooms(socket);

    const handleDeviceData = (payload = {}) => {
      const incoming = asPlainObject(payload);
      const incomingId = getWaterPayloadDeviceId(incoming);
      const incomingController = String(incoming.controllerType || incoming.controller || '').trim().toLowerCase();
      if (!incomingId || !deviceIdsEqual(incomingId, deviceId)) {
        waterSocketDebug('device_data ignored', { incomingId, deviceId, payload: incoming });
        return;
      }
      if (!isWaterControllerToken(incomingController)) {
        waterSocketDebug('device_data ignored by controller', { incomingId, incomingController, payload: incoming });
        return;
      }
      waterSocketDebug('device_data accepted', {
        incomingId,
        deviceId,
        incomingController,
        timestamp:
          incoming.lastRealSensorUpdateAt ||
          incoming.latestTelemetryAt ||
          incoming.lastTelemetryAt ||
          incoming.latestTelemetry?.timestamp ||
          incoming.timestamp ||
          incoming.ts,
      });
      setLiveWaterReading((prev) => mergeWaterReading(prev, incoming, { source: 'socket' }));
    };

    const handleDeviceStatus = (payload = {}) => {
      const incoming = asPlainObject(payload);
      const incomingId = getWaterPayloadDeviceId(incoming);
      if (!incomingId || !deviceIdsEqual(incomingId, deviceId)) return;
      setLiveWaterReading((prev) => ({
        ...asPlainObject(prev),
        deviceId,
        status: incoming.status,
        timestamp: incoming.timestamp || prev?.timestamp,
      }));
    };

    const handleDeviceConfigUpdated = (payload = {}) => {
      const incoming = asPlainObject(payload);
      const incomingId = getWaterPayloadDeviceId(incoming);
      if (!incomingId || !deviceIdsEqual(incomingId, deviceId)) return;
      const nextConfig = asPlainObject(incoming.config);
      if (nextConfig.thresholds || nextConfig.controllerConfig) {
        const controllerConfig = asPlainObject(nextConfig.controllerConfig);
        setWaterThresholds(buildWaterThresholdState(asPlainObject(nextConfig.thresholds), controllerConfig));
      }
    };

    socket.on('device_data', handleDeviceData);
    socket.on('device_status', handleDeviceStatus);
    socket.on('device_config_updated', handleDeviceConfigUpdated);
    return () => {
      socket.off('device_data', handleDeviceData);
      socket.off('device_status', handleDeviceStatus);
      socket.off('device_config_updated', handleDeviceConfigUpdated);
    };
  }, [deviceId]);

  useEffect(() => {
    if (!isAdmin) return;
    requestControllerConfig().finally(fetchConfig);
  }, [deviceId, isAdmin]);

  useEffect(() => {
    if (!autoRefresh) return;
    const latestInt = setInterval(() => {
      if (isRateLimitBackoffActive()) return;
      if (document.visibilityState === 'visible') {
        fetchLatest();
        fetchWaterTelemetryLatest();
      }
    }, 15000);
    const chartInt = setInterval(() => {
      if (isRateLimitBackoffActive()) return;
      if (document.visibilityState === 'visible') {
        fetchSeries();
        fetchConfig();
      }
    }, 60000);
    refreshRefs.current = { latestInt, chartInt };
    return () => {
      clearInterval(latestInt);
      clearInterval(chartInt);
    };
  }, [autoRefresh, deviceId, range, customFrom, customTo]);

  useEffect(() => {
    const handleVis = () => {
      if (document.visibilityState === 'visible') {
        fetchLatest();
        fetchWaterTelemetryLatest();
        fetchSeries();
        fetchConfig();
      }
    };
    document.addEventListener('visibilitychange', handleVis);
    return () => document.removeEventListener('visibilitychange', handleVis);
  }, [deviceId, range, customFrom, customTo]);

  const localeForChart = lang === 'he' ? 'he-IL' : lang === 'es' ? 'es-ES' : lang || 'en-GB';
  const chartLabels = series.map((s) => formatChartRoundHourTime(s.ts, localeForChart));
  const chartLineOptions = {
    responsive: true,
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          callback: (_, index) => formatChartAxisTimeSuppressDuplicates(chartLabels, index),
        },
      },
    },
  };
  const rateData = {
    labels: chartLabels,
    datasets: [
      {
        label: 'in.rate_lpm',
        data: series.map((s) => s.in?.rate_lpm ?? null),
        borderColor: 'blue',
        tension: 0.1,
      },
      {
        label: 'out.rate_lpm',
        data: series.map((s) => s.out?.rate_lpm ?? null),
        borderColor: 'red',
        tension: 0.1,
      },
    ],
  };
  const totalData = {
    labels: chartLabels,
    datasets: [
      {
        label: 'in.total_l',
        data: series.map((s) => s.in?.total_l ?? null),
        borderColor: 'green',
        tension: 0.1,
      },
      {
        label: 'out.total_l',
        data: series.map((s) => s.out?.total_l ?? null),
        borderColor: 'orange',
        tension: 0.1,
      },
    ],
  };
  const liveTemp = liveWaterReading?.ecTemp ?? liveWaterReading?.temp ?? liveWaterReading?.temperature;
  const formatLiveNumber = (value, digits = 1) =>
    value === null || value === undefined || Number.isNaN(Number(value))
      ? '-'
      : Number(value).toFixed(digits);

  return (
    <div className="container mt-3" dir={lang === 'he' ? 'rtl' : 'ltr'}>
      <h3>{info?.name || deviceId}</h3>
      <p>{t('waterPage.idLabel')}: {deviceId}</p>
      <p>{t('status')}: {info?.lastTs ? t('online') : t('unknown')}</p>
      <p>{t('waterPage.firmwareLabel')}: {info?.fwLast || '-'}</p>
      <div className="form-check form-switch mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="autoRefresh"
          checked={autoRefresh}
          onChange={() => setAutoRefresh((p) => !p)}
        />
        <label className="form-check-label" htmlFor="autoRefresh">
          {t('autoRefresh')}
        </label>
      </div>
      <div className="mb-3">
        {['2h', '24h', '7d', 'custom'].map((r) => (
          <button
            key={r}
            className={`btn btn-sm ${actionGapClass} ${range === r ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setRange(r)}
          >
            {rangeLabels[r]}
          </button>
        ))}
      </div>
      {range === 'custom' && (
        <div className="mb-3">
          <label className="form-label">{t('from')}</label>
          <input
            type="datetime-local"
            className="form-control mb-2"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
          />
          <label className="form-label mt-2">{t('to')}</label>
          <input
            type="datetime-local"
            className="form-control"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
          />
        </div>
      )}
      <button
        className="btn btn-primary mb-3"
        onClick={() => {
          fetchLatest();
          fetchWaterTelemetryLatest();
        }}
        disabled={pendingFn}
      >
        {t('refresh')}
      </button>
      {liveWaterReading ? (
        <div className="card mb-3">
          <div className="card-body">
            <h5 className="card-title">{t('waterPage.latestTitle')}</h5>
            <p className="text-muted mb-3">
              {formatDate(liveWaterReading.timestamp || liveWaterReading.ts)}
            </p>
            <div className="row g-2">
              <div className="col-6 col-md-4">
                <strong>pH</strong>
                <div>{formatLiveNumber(liveWaterReading.ph, 2)}</div>
              </div>
              <div className="col-6 col-md-4">
                <strong>EC</strong>
                <div>{formatLiveNumber(liveWaterReading.ec, 3)} mS/cm</div>
              </div>
              <div className="col-6 col-md-4">
                <strong>ORP</strong>
                <div>{formatLiveNumber(liveWaterReading.orp, 0)} mV</div>
              </div>
              <div className="col-6 col-md-4">
                <strong>{t('waterTemperature')}</strong>
                <div>{formatLiveNumber(liveTemp, 1)} °C</div>
              </div>
              <div className="col-6 col-md-4">
                <strong>{t('waterLevel')}</strong>
                <div>{formatLiveNumber(liveWaterReading.level, 0)}</div>
              </div>
              <div className="col-6 col-md-4">
                <strong>{t('waterVolume')}</strong>
                <div>{formatLiveNumber(liveWaterReading.volume, 0)} {t('waterPage.litersShort')}</div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {latest && (
        <div className="mb-3">
          <h5>{t('waterPage.latestTitle')}</h5>
          <p>{formatDate(latest.ts)}</p>
          <p>
            {t('waterPage.inletLine')}: {latest.in?.rate_lpm != null ? `${latest.in.rate_lpm} ${t('waterPage.litersPerMinuteShort')}` : '-'}{' '}
            {latest.in?.total_l != null ? `${latest.in.total_l} ${t('waterPage.litersShort')}` : ''}
          </p>
          <p>
            {t('waterPage.outletLine')}: {latest.out?.rate_lpm != null ? `${latest.out.rate_lpm} ${t('waterPage.litersPerMinuteShort')}` : '-'}{' '}
            {latest.out?.total_l != null ? `${latest.out.total_l} ${t('waterPage.litersShort')}` : ''}
          </p>
        </div>
      )}
      {!latest ? <p className="text-muted">{t('noData')}</p> : null}
      {series.length > 0 && (
        <div className="mb-4">
          <Line data={rateData} options={chartLineOptions} />
          <div className="mt-4">
            <Line data={totalData} options={chartLineOptions} />
          </div>
        </div>
      )}
      {!series.length ? <p className="text-muted">{t('waterPage.noHistory')}</p> : null}
      {summary && (
        <div className="mb-4">
          <h5>{t('summary')}</h5>
          <p>
            {t('inTotalDelta')}: {summary.in?.total_delta_l ?? '-'} {t('waterPage.litersShort')}, {t('avg')}:{' '}
            {summary.in?.avg_rate_lpm ?? '-'} {t('waterPage.litersPerMinuteShort')}
          </p>
          <p>
            {t('outTotalDelta')}: {summary.out?.total_delta_l ?? '-'} {t('waterPage.litersShort')}, {t('avg')}:{' '}
            {summary.out?.avg_rate_lpm ?? '-'} {t('waterPage.litersPerMinuteShort')}
          </p>
        </div>
      )}
      <div className="mb-4">
        <button
          className={`btn btn-outline-secondary ${actionGapClass}`}
          onClick={() => callFn('getReadings')}
          disabled={pendingFn}
        >
          {t('functions.getReadings')}
        </button>
        <button
          className={`btn btn-outline-secondary ${actionGapClass}`}
          onClick={() => callFn('getConfig')}
          disabled={pendingFn}
        >
          {t('getConfig')}
        </button>
        <button
          className={`btn btn-outline-secondary ${actionGapClass}`}
          onClick={() => callFn('pushNow')}
          disabled={pendingFn}
        >
          {t('pushNow')}
        </button>
        <button
          className="btn btn-outline-danger"
          onClick={() => {
            if (window.confirm(t('softResetConfirm'))) callFn('softReset');
          }}
          disabled={pendingFn}
        >
          {t('functions.softReset')}
        </button>
      </div>
      {isAdmin && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">{t('settings')}</h5>
            <div className="mb-3">
              <h6>{t('thresholdValues')}</h6>
              {[
                ['ph', 'pH'],
                ['ec', 'EC'],
                ['orp', 'ORP'],
                ['temp', t('waterTemperature')],
                ['level', t('waterLevel')],
              ].map(([key, label]) => (
                <div className="row g-2 align-items-center mb-2" key={key}>
                  <div className="col-12 col-sm-3">
                    <label className="form-label mb-0">{label}</label>
                  </div>
                  <div className="col-6 col-sm-3">
                    <input
                      type="number"
                      className="form-control"
                      placeholder={t('args.min')}
                      value={waterThresholds[key]?.min ?? ''}
                      onChange={(event) => setWaterThresholdField(key, 'min', event.target.value)}
                    />
                  </div>
                  <div className="col-6 col-sm-3">
                    <input
                      type="number"
                      className="form-control"
                      placeholder={t('args.max')}
                      value={waterThresholds[key]?.max ?? ''}
                      onChange={(event) => setWaterThresholdField(key, 'max', event.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mb-3">
              <h6>{t('tank')}</h6>
              {[
                ['tankMaxHeight', t('maxHeight'), 'cm'],
                ['tankFullVolume', t('waterVolume'), t('waterPage.litersShort')],
              ].map(([key, label, unit]) => (
                <div className="row g-2 align-items-center mb-2" key={key}>
                  <div className="col-12 col-sm-3">
                    <label className="form-label mb-0">{label}</label>
                  </div>
                  <div className="col-8 col-sm-3">
                    <input
                      type="number"
                      className="form-control"
                      value={waterConfigDraft[key] ?? ''}
                      onChange={(event) => setWaterConfigField(key, event.target.value)}
                    />
                  </div>
                  <div className="col-4 col-sm-2 text-muted small">{unit}</div>
                </div>
              ))}
            </div>
            <div className="mb-3">
              <label className="form-label">{t('displayInterval')}</label>
              <div className="input-group">
                <input
                  type="number"
                  step="0.1"
                  className="form-control"
                  value={waterConfigDraft.dMin}
                  onChange={(e) => {
                    setDispInterval(e.target.value);
                    setWaterConfigField('dMin', e.target.value);
                  }}
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={() => callFn('setDisplayInterval', dispInterval)}
                  disabled={pendingFn}
                >
                  {t('apply')}
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">{t('serverInterval')}</label>
              <div className="input-group">
                <input
                  type="number"
                  step="0.1"
                  className="form-control"
                  value={waterConfigDraft.sMin}
                  onChange={(e) => {
                    setSrvInterval(e.target.value);
                    setWaterConfigField('sMin', e.target.value);
                  }}
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={() => callFn('setServerInterval', srvInterval)}
                  disabled={pendingFn}
                >
                  {t('apply')}
                </button>
              </div>
            </div>
            <button
              className="btn btn-primary mb-3"
              onClick={saveWaterSettings}
              disabled={savingWaterSettings || pendingFn}
            >
              {savingWaterSettings ? `${t('save')}...` : t('climateSaveThresholds')}
            </button>
            <div className="mb-3">
              <label className="form-label">{t('calibrateIn')}</label>
              <div className="input-group">
                <input
                  type="number"
                  className="form-control"
                  value={calibIn}
                  onChange={(e) => setCalibIn(e.target.value)}
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={() => callFn('calibrateIn', calibIn)}
                  disabled={pendingFn}
                >
                  {t('apply')}
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">{t('calibrateOut')}</label>
              <div className="input-group">
                <input
                  type="number"
                  className="form-control"
                  value={calibOut}
                  onChange={(e) => setCalibOut(e.target.value)}
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={() => callFn('calibrateOut', calibOut)}
                  disabled={pendingFn}
                >
                  {t('apply')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {config && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">{t('config')}</h5>
            <p>{t('displayIntervalLabel')}: {config.intervals?.display_min ?? '-'}</p>
            <p>{t('serverIntervalLabel')}: {config.intervals?.server_min ?? '-'}</p>
            <p>{t('inPpl')}: {config.in?.ppl ?? '-'}</p>
            <p>{t('outPpl')}: {config.out?.ppl ?? '-'}</p>
            <p>{t('lastTs')}: {formatDate(config.last_ts)}</p>
          </div>
        </div>
      )}
      {msg && (
        <div className={`alert alert-${msg.type === 'error' ? 'danger' : 'success'}`}>{msg.text}</div>
      )}
    </div>
  );
}
